/**
 * rgHtmlRender — rendu sécurisé du HTML brut Lodgify en React nodes.
 *
 * Lodgify stocke les messages avec des balises `<p>`, `<br>`, `<span>`, `<a>` etc.
 * On veut :
 *  - retours à la ligne préservés (`<br>` → \n, `</p>` → \n\n)
 *  - liens cliquables (ouverture nouvel onglet + rel=noopener)
 *  - gras / italique conservés
 *  - toutes les autres balises supprimées (span data-redactor-type, etc.)
 *  - entités HTML décodées (&nbsp;, &amp;, &eacute;, …)
 *  - AUCUN script / iframe / event handler injecté (usage de DOMParser du navigateur,
 *    walk manuel, whitelist stricte — pas de dangerouslySetInnerHTML)
 *
 * Fallback : si `raw` ne contient aucune balise, on linkifie les URL brutes.
 */
import React from "react";

const ALLOWED_INLINE = new Set(["strong", "b", "em", "i", "u"]);
const BLOCK = new Set(["p", "div"]);

function linkifyPlain(text, keyBase) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s<>()]+)/g);
  return parts.map((p, i) => {
    if (!p) return null;
    if (/^https?:\/\//.test(p)) {
      return (
        <a
          key={`${keyBase}-u${i}`}
          href={p}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#075e54", textDecoration: "underline" }}
        >
          {p}
        </a>
      );
    }
    return <React.Fragment key={`${keyBase}-t${i}`}>{p}</React.Fragment>;
  });
}

function walk(node, keyBase) {
  if (!node) return null;
  // Text node
  if (node.nodeType === 3) return linkifyPlain(node.textContent, keyBase);
  // Element node
  if (node.nodeType !== 1) return null;
  const tag = node.tagName.toLowerCase();
  const children = Array.from(node.childNodes)
    .map((c, i) => walk(c, `${keyBase}-${i}`))
    .filter(Boolean);
  if (tag === "br") return <br key={keyBase} />;
  if (tag === "a") {
    const rawHref = node.getAttribute("href") || "";
    // Ne garde que http(s), tel:, sms:, mailto:
    const safe = /^(https?:|tel:|sms:|mailto:)/i.test(rawHref);
    if (!safe) return <React.Fragment key={keyBase}>{children}</React.Fragment>;
    return (
      <a
        key={keyBase}
        href={rawHref}
        target={rawHref.startsWith("http") ? "_blank" : undefined}
        rel="noopener noreferrer"
        style={{ color: "#075e54", textDecoration: "underline", wordBreak: "break-all" }}
      >
        {children.length ? children : rawHref}
      </a>
    );
  }
  if (BLOCK.has(tag)) return <div key={keyBase} style={{ marginBottom: 6 }}>{children}</div>;
  if (ALLOWED_INLINE.has(tag)) {
    const Comp = tag === "b" ? "strong" : tag === "i" ? "em" : tag;
    return <Comp key={keyBase}>{children}</Comp>;
  }
  // Toutes les autres balises : on garde uniquement les enfants (span, ul, li ignorés en tag mais leur contenu passe)
  return <React.Fragment key={keyBase}>{children}</React.Fragment>;
}

export function renderMessageText(raw) {
  if (!raw) return null;
  const hasTags = /<[a-z!][^>]*>/i.test(raw);
  if (!hasTags) return linkifyPlain(raw, "plain");
  try {
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, "text/html");
    const root = doc.body && doc.body.firstChild;
    if (!root) return raw.replace(/<[^>]+>/g, "");
    const nodes = Array.from(root.childNodes)
      .map((c, i) => walk(c, `n${i}`))
      .filter(Boolean);
    return nodes;
  } catch {
    // Fallback ultime : strip tags + décodage entités basique via textarea
    const ta = document.createElement("textarea");
    ta.innerHTML = raw.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "");
    return linkifyPlain(ta.value, "fb");
  }
}
